import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ToolSidebar from "../utils/ToolSideBar";
import { API_URL, authFetch, parseJsonResponse, responseErrorMessage } from "../utils/auth";
import "../../css/partner/Meetings.css";

const durationOptions = [[15, "15 min"], [30, "30 min"], [60, "1 hour"]];
const startOfSchedulingWindow = (date) => {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
};
const addDays = (date, count) => {
  const next = new Date(date);
  next.setDate(next.getDate() + count);
  return next;
};
const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const timeLabel = (date, timeZone) => new Intl.DateTimeFormat([], { hour: "numeric", minute: "2-digit", timeZone }).format(date);

export default function NewMeetingPage({ partner }) {
  const navigate = useNavigate();
  const location = useLocation();
  const requestedStartAt = new URLSearchParams(location.search).get("start_at") || "";
  const [browserTimeZone] = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York");
  const [recipients, setRecipients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [slots, setSlots] = useState([]);
  const [slotAvailability, setSlotAvailability] = useState(null);
  const [settings, setSettings] = useState({ timezone: browserTimeZone });
  const [weekStart, setWeekStart] = useState(startOfSchedulingWindow(new Date()));
  const [form, setForm] = useState({ recipient_id: "", related_project_id: "", duration_minutes: 30, start_at: requestedStartAt, title: "", description: "", timezone: browserTimeZone, meeting_method: "video", location_or_link: "" });
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState("");

  const selectedRecipient = recipients.find((item) => String(item.id) === String(form.recipient_id));
  const selectedSlot = slots.find((slot) => slot.start_at === form.start_at || new Date(slot.start_at).getTime() === new Date(form.start_at).getTime());
  const projectRequired = projects.length > 0;

  useEffect(() => {
    Promise.all([authFetch(`${API_URL}/meetings/recipients/`), authFetch(`${API_URL}/meetings/settings/`)]).then(async ([recipientsRes, settingsRes]) => {
      const recipientsData = await parseJsonResponse(recipientsRes);
      const settingsData = await parseJsonResponse(settingsRes);
      if (recipientsRes.ok) setRecipients(recipientsData?.results || []);
      if (settingsRes.ok) {
        const nextSettings = settingsData || {};
        setSettings(nextSettings);
        setForm((prev) => ({ ...prev, timezone: nextSettings.timezone || browserTimeZone }));
      }
    });
  }, [browserTimeZone]);

  useEffect(() => {
    if (!form.recipient_id) return;
    authFetch(`${API_URL}/meetings/projects/?recipient=${form.recipient_id}`)
      .then(async (res) => (res.ok ? parseJsonResponse(res) : { results: [] }))
      .then((data) => {
        const nextProjects = data.results || [];
        setProjects(nextProjects);
        setForm((prev) => {
          if (nextProjects.some((project) => String(project.id) === String(prev.related_project_id))) return prev;
          return { ...prev, related_project_id: nextProjects.length === 1 ? String(nextProjects[0].id) : "" };
        });
      });
  }, [form.recipient_id]);

  const loadSlots = useCallback(async () => {
    setLoadingSlots(true);
    setError("");
    try {
      const windowStart = weekStart.toISOString().slice(0, 10);
      const res = await authFetch(`${API_URL}/meetings/slots/?recipient=${form.recipient_id}&duration=${form.duration_minutes}&days=7&start_date=${windowStart}`);
      const data = await parseJsonResponse(res);
      if (!res.ok) {
        setSlots([]);
        setSlotAvailability(null);
        setError(responseErrorMessage(res, data, "Unable to load available times."));
        return;
      }
      const nextSlots = data?.results || [];
      setSlots(nextSlots);
      setSlotAvailability(data?.availability || null);
      if (requestedStartAt) {
        const matching = nextSlots.find((slot) => slot.start_at === requestedStartAt || new Date(slot.start_at).getTime() === new Date(requestedStartAt).getTime());
        if (matching) setForm((prev) => (prev.start_at === matching.start_at ? prev : { ...prev, start_at: matching.start_at }));
        else {
          setForm((prev) => {
            if (prev.start_at !== requestedStartAt) return prev;
            setError("That calendar time is not available with this recipient. Choose one of the shared times below.");
            return { ...prev, start_at: "" };
          });
        }
      }
    } catch (err) {
      setSlots([]);
      setSlotAvailability(null);
      setError(err.message || "Unable to load available times.");
    } finally {
      setLoadingSlots(false);
    }
  }, [form.recipient_id, form.duration_minutes, requestedStartAt, weekStart]);

  useEffect(() => {
    if (!form.recipient_id) {
      setSlots([]);
      setSlotAvailability(null);
      return;
    }
    loadSlots();
  }, [form.recipient_id, form.duration_minutes, weekStart, loadSlots]);

  async function submit(e) {
    e.preventDefault();
    if (!form.recipient_id || !form.start_at || !form.title.trim()) {
      setError("Choose a person, time, and meeting title.");
      return;
    }
    if (!selectedSlot) {
      setError("Choose one of the available meeting times shown below.");
      return;
    }
    if (projectRequired && !form.related_project_id) {
      setError("Choose the related project for this meeting.");
      return;
    }
    setSubmitting(true);
    setError("");
    setSuccess("");
    const payload = { ...form, recipient_id: Number(form.recipient_id), duration_minutes: Number(form.duration_minutes), related_project_id: form.related_project_id ? Number(form.related_project_id) : null };
    const res = await authFetch(`${API_URL}/meetings/`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
    const data = await parseJsonResponse(res);
    if (res.ok) {
      const meeting = data?.meeting || data;
      setSuccess(data?.message || "Meeting request sent.");
      setTimeout(() => navigate(`/meetings/${meeting.id}`), 450);
    } else {
      setError(responseErrorMessage(res, data, "Unable to request meeting."));
      setSubmitting(false);
    }
  }

  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, index) => addDays(weekStart, index)), [weekStart]);
  const visibleSlots = slots.filter((slot) => {
    const start = new Date(slot.start_at);
    return start >= weekDays[0] && start < addDays(weekDays[6], 1);
  });
  const requesterHasWeeklyAvailability = Boolean(slotAvailability?.requester?.has_weekly_availability);
  const recipientHasWeeklyAvailability = Boolean(slotAvailability?.recipient?.has_weekly_availability);
  const requesterUsesDefaultAvailability = Boolean(slotAvailability?.requester?.uses_default_weekly_availability);
  const recipientUsesDefaultAvailability = Boolean(slotAvailability?.recipient?.uses_default_weekly_availability);
  const bothConfigured = requesterHasWeeklyAvailability && recipientHasWeeklyAvailability;
  const availabilityHeading = bothConfigured ? "Overlapping Availability" : "Available Times";
  const defaultAvailabilityNote = selectedRecipient && requesterUsesDefaultAvailability && recipientUsesDefaultAvailability
    ? `Neither person has set availability preferences, so times are based on default scheduling hours and existing calendar conflicts.`
    : selectedRecipient && !recipientHasWeeklyAvailability
      ? `${selectedRecipient.short_name || selectedRecipient.name} hasn't set availability preferences, so available times are based on your schedule and existing calendar conflicts.`
      : selectedRecipient && !requesterHasWeeklyAvailability
        ? `You haven't set availability preferences, so available times are based on ${selectedRecipient.short_name || selectedRecipient.name}'s schedule and existing calendar conflicts.`
        : "";

  return (
    <div className="staff-shell">
      <ToolSidebar active="meetings" isPartner={partner} />
      <main className="staff-page meetings-page request-meeting-page">
        <header className="meeting-hero compact-hero"><div><span>Request</span><h1>New Meeting</h1><p>Find the times where both calendars overlap.</p></div></header>
        {error && <p className="meeting-error">{error}</p>}
        {success && <p className="meeting-notice">{success}</p>}

        <section className="request-steps">
          <article className="staff-card request-step">
            <span>Step 1</span>
            <h2>Select Person</h2>
            <select value={form.recipient_id} onChange={(e) => setForm({ ...form, recipient_id: e.target.value, start_at: "" })}><option value="">Choose an approved contact</option>{recipients.map((r) => <option key={r.id} value={r.id}>{r.short_name || r.name} · {r.role_label || r.role}</option>)}</select>
          </article>
          <article className="staff-card request-step">
            <span>Step 2</span>
            <h2>Select Duration</h2>
            <div className="duration-segments">{durationOptions.map(([value, label]) => <button type="button" key={value} className={Number(form.duration_minutes) === value ? "active" : ""} onClick={() => setForm({ ...form, duration_minutes: value, start_at: "" })}>{label}</button>)}</div>
          </article>
          <article className="staff-card request-step">
            <span>Step 3</span>
            <h2>Related Project</h2>
            <select value={form.related_project_id} onChange={(e) => setForm({ ...form, related_project_id: e.target.value })}><option value="">{projectRequired ? "Select project" : "No project"}</option>{projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select>
          </article>
        </section>

        <section className="staff-card overlap-card">
          <div className="settings-section-heading">
            <div><h2>{availabilityHeading}</h2><p>Your time zone: {settings.timezone || browserTimeZone}{selectedRecipient && selectedRecipient.role ? ` | ${selectedRecipient.short_name || selectedRecipient.name}'s availability included` : ""}</p></div>
            <div className="calendar-nav"><button type="button" onClick={() => setWeekStart(addDays(weekStart, -7))}>Previous</button><button type="button" onClick={() => setWeekStart(startOfSchedulingWindow(new Date()))}>Today</button><button type="button" onClick={() => setWeekStart(addDays(weekStart, 7))}>Next</button></div>
          </div>
          <div className="calendar-legend"><span><i className="both" /> Both available</span><span><i className="partial" /> One person only</span><span><i className="unavailable" /> Not available</span></div>
          {defaultAvailabilityNote && <p className="meeting-context-note">{defaultAvailabilityNote}</p>}
          {!form.recipient_id ? <p className="compact-empty">Select a person to see shared availability.</p> : loadingSlots ? <div className="availability-loading" role="status" aria-live="polite"><span className="meeting-spinner" aria-hidden="true" /><strong>Checking shared calendars...</strong><small>Finding times that work for both people.</small></div> : visibleSlots.length === 0 ? <p className="compact-empty">No available times for this week.</p> : (
            <div className="overlap-week">
              {weekDays.map((day) => {
                const daySlots = visibleSlots.filter((slot) => new Date(slot.start_at).toDateString() === day.toDateString());
                return <article className="overlap-day" key={day.toISOString()}><h3>{dayNames[day.getDay()]} <span>{day.toLocaleDateString([], { month: "short", day: "numeric" })}</span></h3>{daySlots.length === 0 ? <span className="no-slots">No shared times</span> : daySlots.map((slot) => <button type="button" className={form.start_at === slot.start_at ? "selected" : ""} key={slot.start_at} onClick={() => setForm({ ...form, start_at: slot.start_at })}><strong>{timeLabel(new Date(slot.start_at), settings.timezone || browserTimeZone)}</strong>{slot.viewer_timezone !== slot.recipient_timezone && <small>{slot.recipient_label}</small>}<em>Both available</em></button>)}</article>;
              })}
            </div>
          )}
        </section>

        <form className="staff-card meeting-form request-details-form" onSubmit={submit}>
          <div className="settings-section-heading"><h2>Meeting Details</h2></div>
          <label>Title<input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Project check-in" /></label>
          <label>Agenda<textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What should this meeting cover?" /></label>
          <label>Method<select value={form.meeting_method} onChange={(e) => setForm({ ...form, meeting_method: e.target.value })}><option value="video">Video meeting</option><option value="phone">Phone call</option><option value="in_person">In person</option></select></label>
          <label>Link, phone, or location<input value={form.location_or_link} onChange={(e) => setForm({ ...form, location_or_link: e.target.value })} /></label>
          {selectedSlot && selectedRecipient && <aside className="request-summary"><h3>Request Summary</h3><dl><dt>Meeting with</dt><dd>{selectedRecipient.short_name || selectedRecipient.name} · {selectedRecipient.role_label || selectedRecipient.role}</dd><dt>Date</dt><dd>{new Date(selectedSlot.start_at).toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" })}</dd><dt>Time</dt><dd>{timeLabel(new Date(selectedSlot.start_at), settings.timezone || browserTimeZone)} - {timeLabel(new Date(selectedSlot.end_at), settings.timezone || browserTimeZone)}{selectedSlot.viewer_timezone !== selectedSlot.recipient_timezone && <small>{selectedSlot.recipient_label}</small>}</dd><dt>Duration</dt><dd>{form.duration_minutes} minutes</dd><dt>Status after submission</dt><dd>{selectedRecipient.role === "Partner" && (localStorage.getItem("is_superuser") === "true" || localStorage.getItem("is_staff") === "true") ? "Confirmed" : `${selectedRecipient.short_name || selectedRecipient.name}'s approval pending`}</dd></dl></aside>}
          <button className="btn btn-primary" disabled={submitting}>{submitting ? "Sending request..." : "Request Meeting"}</button>
        </form>
      </main>
    </div>
  );
}
